import type { GameState } from '../game/types';
import { ASSISTANTS } from './registry';
import { isAssistantHired, isAssistantUnlocked } from './types';

export * from './types';
export * from './registry';
export * from './flightSpeed';
export * from './cabinConfig';
export * from './crewSalary';
export * from './financial';

/** 检查并解锁满足条件的助理。应在每周模拟后调用。 */
export function unlockAssistants(state: GameState): GameState {
  const next = { ...state };
  const unlockedIds = new Set(next.assistants.unlockedIds);
  for (const assistant of ASSISTANTS) {
    if (!unlockedIds.has(assistant.id) && assistant.unlocked(next)) {
      unlockedIds.add(assistant.id);
    }
  }
  next.assistants = {
    ...next.assistants,
    unlockedIds: Array.from(unlockedIds),
  };
  return next;
}

export function recruitAssistant(state: GameState, assistantId: string): GameState {
  const assistant = ASSISTANTS.find((a) => a.id === assistantId);
  if (!assistant) throw new Error('助理不存在');
  if (!isAssistantUnlocked(state, assistantId)) throw new Error('该助理尚未解锁');
  if (isAssistantHired(state, assistantId)) throw new Error('该助理已在岗');
  if (state.cash < assistant.recruitCost) throw new Error(`现金不足，招募费用 ${assistant.recruitCost.toLocaleString('zh-CN')}`);

  const next = { ...state };
  next.cash -= assistant.recruitCost;
  next.assistants = {
    ...next.assistants,
    hired: [
      ...next.assistants.hired,
      { id: assistantId, hiredWeek: next.week, weeklySalary: assistant.weeklySalary, active: true },
    ],
  };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'operating_expense',
    cashAmount: -assistant.recruitCost,
    profitAmount: -assistant.recruitCost,
    objectId: assistantId,
    label: `招募 ${assistant.name}`,
  });
  return next;
}

export function dismissAssistant(state: GameState, assistantId: string): GameState {
  if (!isAssistantHired(state, assistantId)) return state;
  return {
    ...state,
    assistants: {
      ...state.assistants,
      hired: state.assistants.hired.map((h) => (h.id === assistantId ? { ...h, active: false } : h)),
    },
  };
}

export function weeklyAssistantSalaries(state: GameState): number {
  return state.assistants.hired
    .filter((h) => h.active)
    .reduce((sum, h) => sum + h.weeklySalary, 0);
}
